import { ProductFaq } from "../types";

const img = (n: string) => `/images/faq/v2/${n}.png`;

export const protectV2EN: ProductFaq = {
	title: "Frequently Asked Questions and Troubleshooting",
	downloads: [
		{ title: "FAQ and Troubleshooting (English)", path: "/documents/downloads/2026-10-21_Protect_FAQ-Troubleshooting-EN.pdf" },
		{ title: "Quick Start Guide", path: "/documents/downloads/2026-06-16_Protect_QuickStartGuide_007570-R2A.pdf" },
		{ title: "Reference Guide", path: "/documents/downloads/2026-07-06_Protect_ReferenceGuide-ReferentieGids_007562-R2A.pdf" },
	],
	categories: [
		{
			id: "pairing",
			title: "Pairing & Connectivity",
			questions: [
				{
					id: "pair-first",
					title: "How do I pair my EarsOnly Protect for the first time?",
					blocks: [
						{
							t: "ol",
							items: [
								"Install the myProtect app through the QR code on your Quick Start Guide (or via [this link](https://play.google.com/store/apps/details?id=nl.earsonly.myprotect)).",
								"Follow the instructions in the app. The Earbuds will pair with your device automatically.",
							],
						},
						{ t: "img", src: img("pairing-qr"), alt: "Scan the QR code, then open the myProtect app" },
					],
				},
				{
					id: "pair-new",
					title: "How do I pair my EarsOnly Protect to a new device?",
					blocks: [
						{
							t: "ol",
							items: [
								"Ensure that the Charging Case is charged to at least 10% (see {{q:battery-check}}).",
								"Make sure the Earbuds are properly placed in the Charging Case.",
								"Press and hold the button on the Charging Case for at least 3 seconds. Wait until the LEDs of both Earbuds turn blue; this may take up to 5 seconds.",
								"Open the Bluetooth settings on your device and select the option to connect or pair a new device.",
								"Select “EarsOnly Protect” from the list to pair and connect.",
							],
						},
						{ t: "img", src: img("pairing-device"), alt: "Pairing the Earbuds through the Bluetooth settings of your device" },
					],
				},
				{
					id: "pair-fail",
					title: "Why can’t I pair my Earbuds to a device?",
					blocks: [
						{
							t: "ol",
							items: [
								"Make sure the Charging Case is charged to at least 10% (see {{q:battery-check}}).",
								"Make sure both Earbuds are correctly placed in the Charging Case.",
								{
									text: "Press the button on the Charging Case for three seconds and check the LEDs:",
									sub: [
										"If the Charging Case LEDs and both Earbud LEDs light up: both Earbuds are connected to the Charging Case and can now be paired. (See: {{q:pair-new}})",
										"If the Charging Case LED lights up, but one or both Earbud LEDs do not: one or both Earbuds are not connected to the Charging Case. See next steps.",
									],
								},
							],
						},
						{ t: "h", text: "If only the Charging Case LED lights up" },
						{
							t: "ol",
							items: [
								"Remove both Earbuds from the Charging Case.",
								"Wait 5 seconds.",
								"Place both Earbuds back into the Charging Case.",
								"Check that the Earbud LEDs light up when each Earbud is placed in the Case.",
								"Press the button again for at least 3 seconds until the Earbud LEDs light up.",
								"If both Earbud LEDs still don’t light up, reset both Earbuds and the Charging Case, then start again from step 1. (See: {{q:reset-earbuds}} and {{q:reset-case}})",
								"If both Earbud LEDs light up now, pair the Earbuds to your device. (See: {{q:pair-new}})",
							],
						},
						{ t: "h", text: "Still unable to pair?" },
						{ t: "p", text: "If the LEDs on both the Earbuds still don’t light up, one or both Earbuds may not be making proper contact with the Charging Case." },
						{
							t: "ol",
							items: [
								"Clean the charging contacts on both the Earbuds and the Charging Case with a slightly damp cloth. (Wait until the charging contacts are fully dry before placing the Earbuds back in the Charging Case.)",
								"With the Earbuds in the Charging Case, press the button again and check whether all three LEDs (both on the Earbuds and the Charging Case) light up.",
								"If both Earbud LEDs and the Charging Case LED light up, try pairing again.",
							],
						},
						{ t: "p", text: "If the problem persists, reset both Earbuds and the Charging Case, then try pairing again. (See: {{q:reset-earbuds}} and {{q:reset-case}})" },
					],
				},
				{
					id: "connect-fail",
					title: "Why won’t the Earbuds connect to my device?",
					blocks: [
						{
							t: "ol",
							items: [
								"Place the Earbuds back in the Charging Case, wait 10 seconds and take them out again.",
								"In your device’s Bluetooth menu (under settings), make sure the Earbuds are paired with your device. If the Earbuds aren’t paired yet, pair them to your device. Follow the pairing instructions. (See: {{q:pair-new}})",
								"If this doesn’t work, perform a reset of the Earbuds (see: {{q:reset-earbuds}}) and try again.",
								"Reboot the device you are trying to connect with and try again.",
								"If this doesn’t work, remove the Earbuds from the Bluetooth pairing list on your device and re-pair the Earbuds to your device (see: {{q:pair-new}}).",
							],
						},
					],
				},
				{
					id: "disconnecting",
					title: "Why do my Earbuds keep disconnecting?",
					blocks: [
						{
							t: "ol",
							items: [
								"In your device’s Bluetooth menu, check if the Earbuds are still connected.",
								"Try resetting the Earbuds by holding the button on both Earbuds for 15 seconds.",
							],
						},
						{ t: "note", kind: "note", text: "Preferred settings will not be lost." },
						{ t: "h", text: "Do the Earbuds still disconnect?" },
						{ t: "p", text: "Other devices (like smartwatches) can interfere with the connection. Turning off Bluetooth on other nearby devices may solve this." },
					],
				},
			],
		},
		{
			id: "use",
			title: "Use & Controls",
			questions: [
				{
					id: "gestures",
					title: "How to control the Earbuds using tapping gestures?",
					blocks: [
						{ t: "p", text: "The Earbuds come equipped with two main control functions: button press (click) and tap." },
						{
							t: "table",
							head: ["Gesture", "Idle or streaming", "During (incoming) phone call"],
							rows: [
								["Short button press left or right Earbud", "Pause/play audio", "Accept call / End call"],
								["Short button press twice left Earbud", "Switch to previous Damping Mode", "Decline call"],
								["Short button press twice right Earbud", "Switch to next Damping Mode", "Decline call"],
								["Long button press left Earbud", "Decrease audio volume", "Decrease audio volume"],
								["Long button press right Earbud", "Increase audio volume", "Increase audio volume"],
								["Tap twice left Earbud", "Previous track", "Mute mic"],
								["Tap twice right Earbud", "Next track", "Mute mic"],
							],
						},
						{ t: "img", src: img("tap-controls"), alt: "Where to press and tap on the Earbuds" },
					],
				},
				{
					id: "button-config",
					title: "Can I change the button configuration?",
					blocks: [
						{ t: "p", text: "You can change the action assigned to each button in the myProtect app." },
						{
							t: "ol",
							items: [
								"Connect your Earbuds to your device and to the app. Location Services must be enabled for this to work correctly.",
								"Go to Settings > Button Configuration.",
								"You can now change the action assigned to each button.",
							],
						},
					],
				},
				{
					id: "wear",
					title: "How do I correctly wear EarsOnly Protect?",
					blocks: [
						{ t: "p", text: "For the optimal fit, insert the Earbuds into your ear as follows." },
						{
							t: "ol",
							items: [
								"Identify the left and right Earbuds. The Earbud with the red marking goes into your right ear; the one with blue marking goes into your left.",
								"Push the end with the color marking into the ear canal and twist the Earbud slightly back so it slides correctly into place.",
								"If needed, pull the top of your ear up a bit, so the Earbud can slide in easier.",
							],
						},
						{ t: "img", src: img("wear-earbuds"), alt: "Inserting the Earbud: push and twist slightly back" },
					],
				},
			],
		},
		{
			id: "charging",
			title: "Charging & Battery",
			questions: [
				{
					id: "charge",
					title: "How do I charge my Earbuds and Charging Case?",
					blocks: [
						{ t: "h", text: "The Charging Case" },
						{ t: "p", text: "The Case can be charged in either of the following ways:" },
						{
							t: "ul",
							items: [
								"**USB-C:** connect the Charging Case using the included USB-C cable and a USB charger or USB port.",
								"**Wireless Charging:** place the Charging Case on a compatible wireless charger.",
							],
						},
						{ t: "h", text: "The Earbuds" },
						{ t: "p", text: "Place the left and right Earbuds on their corresponding charging contacts in the Charging Case, as shown in the image." },
						{ t: "note", kind: "warning", text: "Make sure the charging contacts on both the Earbuds and the Charging Case are clean and free from dirt. If the Earbuds do not make proper contact, they will not charge." },
						{ t: "p", text: "See {{q:clean}} for instructions on cleaning the charging contacts." },
						{ t: "img", src: img("charging"), alt: "Earbuds placed on the charging contacts in the Charging Case" },
					],
				},
				{
					id: "battery-check",
					title: "How can I check the battery status of my Earbuds and Charging Case?",
					blocks: [
						{
							t: "ol",
							items: [
								"Open the Charging Case.",
								"Insert both Earbuds into the Charging Case.",
								"The LEDs on both Earbuds automatically indicate their individual battery levels.",
								"The LED on the Charging Case indicates the battery level of the Charging Case.",
							],
						},
						{ t: "note", kind: "note", text: "The battery levels of the Earbuds and Charging Case can also be checked by shortly pressing their respective buttons." },
					],
				},
				{
					id: "leds",
					title: "What do the LEDs on the Earbuds and Charging Case mean?",
					blocks: [
						{ t: "p", text: "The LEDs on the Earbuds and Charging Case indicate the current battery level or charging status." },
						{ t: "img", src: img("led-status"), alt: "Battery level LED colours: green 80% or more, orange 10 to 80%, flashing orange below 10%" },
						{ t: "h", text: "Battery level" },
						{
							t: "ul",
							items: [
								"If the LED on the Earbud or Charging Case is **green**, this means the battery is charged for 80% or more.",
								"If the LED on the Earbud or Charging Case is **orange**, this means the battery is charged between 10 and 80%.",
								"If the battery is **orange and flashing** on and off, this means the battery level is below 10%.",
							],
						},
						{ t: "h", text: "Charging status" },
						{
							t: "ul",
							items: [
								"When the Case is connected to a (wireless) charger, the LEDs on the Case will show a charging animation with the LEDs jumping from left to right.",
								"After closing the lid of the Charging Case, charging will continue but the animation will shortly turn off. The charging status can be checked by opening the lid of the Charging Case again.",
								"When fully charged, all three LEDs on the Charging Case turn green before turning off. When opening the lid of the Case, the LED will turn green again.",
							],
						},
					],
				},
				{
					id: "not-charged",
					title: "Why are my Earbuds not fully charged after a long time of charging?",
					blocks: [
						{
							t: "ol",
							items: [
								"Make sure the Earbuds are properly placed inside the Charging Case.",
								"Make sure the Charging Case still has more than 10% battery (see: {{q:battery-check}})",
								"Clean the charging contacts of the Earbuds and the Charging Case with a lightly damp cloth. Wait until they are fully dry before placing the Earbuds back in the Charging Case.",
								"If this doesn’t work, perform a reset of the Earbuds and Charging Case (see: {{q:reset-earbuds}} and {{q:reset-case}}) and try again.",
							],
						},
					],
				},
			],
		},
		{
			id: "audio",
			title: "Audio & Connection",
			questions: [
				{
					id: "volume-limit",
					title: "Why does my phone limit the audio volume of my EarsOnly Protect?",
					blocks: [
						{ t: "p", text: "EarsOnly Protect limits media sound to safe listening levels to help maintain healthy hearing. If your device also has an audio volume limit enabled, it may unnecessarily limit the maximum volume of your Earbuds. If available on your device, disable the device’s audio volume limit." },
					],
				},
				{
					id: "no-sound",
					title: "Why do I hear (almost) no sound on the Earbud(s)?",
					blocks: [
						{ t: "h", text: "Are the Earbuds connected?" },
						{
							t: "ol",
							items: [
								"In your device’s Bluetooth menu (under settings), make sure the Earbuds are connected to the audio source.",
								"Make sure the audio stream is playing.",
								"Place the Earbuds back in the Charging Case, wait 10 seconds and take them out again.",
								"If the audio stream stopped playing, start the audio stream again.",
							],
						},
						{ t: "h", text: "Check the sound canal" },
						{
							t: "ol",
							items: [
								"Check whether the sound canals of both Earbuds are still open and free of dirt or earwax.",
								"In case the sound canals are (partially) blocked, clean them with the included earwax removal pen. (See: {{q:clean}})",
							],
						},
						{ t: "h", text: "Still no audio?" },
						{ t: "ol", items: ["Perform a reset of the Earbuds and try again (see: {{q:reset-earbuds}})."] },
					],
				},
				{
					id: "out-of-sync",
					title: "Why is my audio out of sync?",
					blocks: [
						{
							t: "ol",
							items: [
								"Place the Earbuds in the Charging Case, wait 10 seconds and take them out again.",
								"If this doesn’t work, perform a reset of the Earbuds and try again (see: {{q:reset-earbuds}}).",
							],
						},
					],
				},
			],
		},
		{
			id: "reset",
			title: "Resetting",
			questions: [
				{
					id: "reset-earbuds",
					title: "How to reset the Earbuds?",
					blocks: [
						{
							t: "ol",
							items: [
								"Take both Earbuds out of the Charging Case.",
								"Place the Earbuds in your ear.",
								"Press and hold both the Earbud buttons simultaneously for 15 seconds.",
								"A startup sound can be heard when the Earbuds have reset.",
							],
						},
						{ t: "note", kind: "note", text: "Preferred settings will not be lost." },
					],
				},
				{
					id: "reset-case",
					title: "How to reset the Charging Case?",
					blocks: [
						{
							t: "ol",
							items: [
								"Press and hold the Charging Case button for 15 seconds.",
								"The Charging Case LED will turn green when the reset is successful.",
							],
						},
						{ t: "note", kind: "note", text: "Preferred settings will not be lost." },
					],
				},
				{
					id: "factory-reset",
					title: "How to do a factory reset?",
					blocks: [
						{ t: "note", kind: "warning", text: "By performing a factory reset you will lose your preferred settings and the pairing information with your devices." },
						{
							t: "ol",
							items: [
								"Make sure the Charging Case has more than 10% charge.",
								"Make sure the Earbuds are properly placed inside the Charging Case.",
								"Press and hold the button on the Charging Case for at least 3 seconds. Wait until the LEDs on the Charging Case and the Earbuds turn blue. This may take up to 5 seconds.",
								"Quickly double press the Charging Case button. The Earbud LEDs should now show a purple color.",
								"When the LEDs turn blue again the factory reset is successful.",
								"Remove the old pairing from your device’s Bluetooth menu.",
								"You can now pair your EarsOnly Protect to a device again.",
							],
						},
					],
				},
			],
		},
		{
			id: "maintenance",
			title: "Maintenance",
			questions: [
				{
					id: "clean",
					title: "How do I clean my EarsOnly Protect?",
					blocks: [
						{ t: "note", kind: "warning", text: "Do not use any aggressive cleaning agents or solvents, like benzene, thinner, dishwashing liquid or alcohol as they may damage the surface. Contact the manufacturer for additional information." },
						{ t: "h", text: "Daily maintenance" },
						{
							t: "ul",
							items: [
								"Wipe the outer surface of the Earbuds with a slightly damp cloth. Ensure that the charging contacts are free from dirt.",
								"Wipe the inside and outside of the Charging Case with a slightly damp cloth. Ensure that the charging contacts are free from dirt.",
							],
						},
						{ t: "note", kind: "important", text: "Before placing the Earbuds back into the Charging Case, ensure the Earbuds and Charging Case are completely dry." },
						{ t: "h", text: "Weekly cleaning" },
						{
							t: "ul",
							items: [
								"Check to make sure the Earbuds’ sound canals are open and free of dirt.",
								"If not, use the supplied earwax removal pen to remove any dirt from the sound canal.",
							],
						},
						{ t: "note", kind: "note", text: "Earwax in the Earbuds’ sound canal can negatively impact the sound quality and volume." },
						{ t: "note", kind: "note", text: "The aforementioned cleaning instructions are the manufacturer’s advice: clean as needed." },
						{ t: "img", src: img("cleaning"), alt: "Cleaning the sound canal of the Earbud with the earwax removal pen" },
					],
				},
			],
		},
	],
};
